/**
 * Plan of Record — create view. Creating a PoR seeds the 10 canonical sections
 * and the 6 KPI cards (via the create_por RPC) and lands the user on the
 * executive dashboard.
 */

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { createPor } from '../../lib/planOfRecordService';
import { PorCard, PrimaryButton, GhostButton } from '../../components/plan-of-record/porUi';
import { usePlanInstrumentNav } from '../../contexts/PlanInstrumentNavContext';

export default function PorCreate() {
  const navigate = useNavigate();
  const { instrumentListHref, porHref } = usePlanInstrumentNav();
  const [title, setTitle] = useState('');
  const [horizonShort, setHorizonShort] = useState('90d');
  const [horizonLong, setHorizonLong] = useState('12m');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const valid = title.trim().length > 0;

  const handleCreate = async () => {
    if (!valid) {
      setError('A title is required.');
      return;
    }
    setBusy(true);
    setError(null);
    const { data, error: err } = await createPor({
      title: title.trim(),
      horizon_short: horizonShort.trim() || null,
      horizon_long: horizonLong.trim() || null,
    });
    if (err || !data) {
      setError(err ?? 'Failed to create Plan of Record.');
      setBusy(false);
      return;
    }
    navigate(porHref(data.por_id, 'dashboard'));
  };

  const field = 'w-full text-sm  border border-[var(--rule)] px-3 py-2 focus:border-[var(--slate)] focus:ring-2 focus:ring-indigo-100 focus:outline-none';
  const label = 'block text-xs font-semibold uppercase tracking-wide text-[var(--text-ter)] mb-1.5';

  return (
    <>
      <button
        onClick={() => navigate(instrumentListHref)}
        className="inline-flex items-center gap-1.5 text-sm text-[var(--text-ter)] hover:text-[var(--text-sec)] mb-4"
      >
        <ArrowLeft className="h-4 w-4" /> All Plans of Record
      </button>

      <h1 className="text-2xl font-semibold text-[var(--text-pri)] mb-1">New Plan of Record</h1>
      <p className="text-sm text-[var(--text-ter)] mb-6 max-w-xl">
        This creates the record, seeds the ten canonical sections (Vision through Appendices), and
        prepares the six executive KPI cards. You fill in the substance from the dashboard and the
        section editor.
      </p>

      <PorCard className="max-w-xl">
        <div className="space-y-4">
          <div>
            <label className={label}>Title</label>
            <input
              value={title}
              onChange={(e) => {
                setTitle(e.target.value);
                if (error) setError(null);
              }}
              placeholder='e.g. "Priorities.ai — FY26 Plan of Record"'
              className={field}
              autoFocus
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={label}>Short horizon</label>
              <input
                value={horizonShort}
                onChange={(e) => setHorizonShort(e.target.value)}
                placeholder="90d"
                className={field}
              />
            </div>
            <div>
              <label className={label}>Long horizon</label>
              <input
                value={horizonLong}
                onChange={(e) => setHorizonLong(e.target.value)}
                placeholder="12m"
                className={field}
              />
            </div>
          </div>
          {error && <p className="text-xs text-luminous-coral-700">{error}</p>}
          <div className="flex justify-end gap-2 pt-2">
            <GhostButton onClick={() => navigate(instrumentListHref)} disabled={busy}>
              Cancel
            </GhostButton>
            <PrimaryButton onClick={handleCreate} disabled={busy || !valid}>
              {busy ? 'Creating…' : 'Create Plan of Record'}
            </PrimaryButton>
          </div>
        </div>
      </PorCard>
    </>
  );
}
